import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCurrentUserContext } from '@/contexts';
import { gqlClient } from '@/lib';
import type { GetLibraryInput } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createLibraryQueryKey } from '@/utils/queryKeys';
import type {
  GetLibraryQuery,
  GetLibraryWithEventQuery,
} from './useLibrary.generated';

// Base query without isEventPresent
const baseQuery = gql`
  query GetLibrary($input: GetLibraryInput) {
    getLibrary(input: $input) {
      id
      role
      type
      title
      imageUrl
      editUrl
      publicUrl
      openedAt
      createdAt
      isSaved
      timeZone
      chatId
      organizationId
      permissions {
        role
        all {
          id
          name
          email
          avatar
        }
      }
      styles {
        hue
        saturation
        lightness
        bold
        vibe
        colorPalette
        primaryColor
        accentBackgroundEnabled
      }
    }
  }
`;

// Query with isEventPresent when eventId is provided
const queryWithEvent = gql`
  query GetLibraryWithEvent($eventId: String!, $input: GetLibraryInput) {
    getLibrary(input: $input) {
      id
      role
      type
      title
      imageUrl
      editUrl
      publicUrl
      openedAt
      createdAt
      isSaved
      timeZone
      isEventPresent(eventId: $eventId)
      chatId
      organizationId
      permissions {
        role
        all {
          id
          name
          email
          avatar
        }
      }
      styles {
        hue
        saturation
        lightness
        bold
        vibe
        colorPalette
        primaryColor
        accentBackgroundEnabled
      }
    }
  }
`;

type LibraryQueryResult = GetLibraryQuery | GetLibraryWithEventQuery;

export type LibraryItem = GetLibraryQuery['getLibrary'][number] & {
  contextId: string;
};

const selector = (
  data: GetLibraryQuery | GetLibraryWithEventQuery
): LibraryItem[] => {
  return data.getLibrary.map((item) => ({
    ...item,
    // the `type` in the library schema differs from the type in a schedule
    contextId: 'library-' + item.id,
  }));
};

// Callers own the scope: pass `organizationId` for one org's library
// (the Dashboard tab), `allOrganizations` for everything the user can
// reach (destination pickers), or neither for the personal library.
export const useLibrary = (
  eventId?: string,
  input?: GetLibraryInput,
  options?: { enabled?: boolean }
) => {
  const { isAuthenticated } = useCurrentUserContext();

  const queryKey = createLibraryQueryKey(eventId, input);

  const { isPending, ...rest } = useQuery<
    LibraryQueryResult,
    QueryError,
    LibraryItem[]
  >({
    queryKey,
    select: selector,
    enabled: isAuthenticated && (options?.enabled ?? true),
    queryFn: async (): Promise<LibraryQueryResult> => {
      // Use different queries based on whether eventId is provided
      if (eventId) {
        const response = await gqlClient.request<
          GetLibraryWithEventQuery,
          { eventId: string; input?: GetLibraryInput }
        >(queryWithEvent, { eventId, input });
        return response;
      } else {
        const response = await gqlClient.request<
          GetLibraryQuery,
          { input?: GetLibraryInput }
        >(baseQuery, { input });
        return response;
      }
    },
  });

  return {
    ...rest,
    isPending: isPending && isAuthenticated,
  };
};
